import { useNavigate } from 'react-router-dom';
import {
  Drawer,
  Stack,
  Group,
  Text,
  Button,
  ActionIcon,
  NumberInput,
  Divider,
  ScrollArea,
} from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import { useCart, useUpdateCartItem, useRemoveCartItem } from '@/hooks/usePortalApi';
import EmptyState from '@/components/EmptyState';

interface CartDrawerProps {
  opened: boolean;
  onClose: () => void;
}

export default function CartDrawer({ opened, onClose }: CartDrawerProps) {
  const navigate = useNavigate();
  const { data: cart } = useCart();
  const updateItem = useUpdateCartItem();
  const removeItem = useRemoveCartItem();

  const goToCart = () => {
    onClose();
    navigate('/cart');
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title="Shopping Cart"
      position="right"
      size="sm"
    >
      {!cart?.items?.length ? (
        <EmptyState
          title="Your cart is empty"
          description="Browse our catalog to find products"
          action={
            <Button onClick={() => { onClose(); navigate('/shop'); }}>
              Start Shopping
            </Button>
          }
        />
      ) : (
        <Stack h="100%" justify="space-between">
          <ScrollArea style={{ flex: 1 }}>
            <Stack gap="sm">
              {cart.items.map((item) => (
                <Group key={item.id} justify="space-between" wrap="nowrap">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={500} lineClamp={1}>
                      {item.product_name}
                    </Text>
                    <Text size="xs" c="dimmed">
                      ${parseFloat(item.unit_price).toFixed(2)} / {item.unit}
                    </Text>
                  </div>
                  <Group gap="xs" wrap="nowrap">
                    <NumberInput
                      value={item.quantity}
                      onChange={(v) =>
                        updateItem.mutate({ itemId: item.id, quantity: Number(v) || 1 })
                      }
                      min={1}
                      w={70}
                      size="xs"
                      aria-label={`Quantity for ${item.product_name}`}
                    />
                    <Text size="sm" fw={500} w={60} ta="right">
                      ${parseFloat(item.line_total).toFixed(2)}
                    </Text>
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      size="sm"
                      onClick={() => removeItem.mutate(item.id)}
                      aria-label={`Remove ${item.product_name} from cart`}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Group>
                </Group>
              ))}
            </Stack>
          </ScrollArea>

          <div>
            <Divider my="sm" />
            <Group justify="space-between" mb="sm">
              <Text fw={600}>Total</Text>
              <Text fw={700} size="lg">
                ${parseFloat(cart.total).toFixed(2)}
              </Text>
            </Group>
            <Button fullWidth onClick={goToCart}>
              View Cart & Checkout
            </Button>
          </div>
        </Stack>
      )}
    </Drawer>
  );
}
